/**
 * Forgot-password page for dashboard users. Accepts an email and triggers
 * the backend's reset-email flow. Always shows a success state after
 * submission (so the page can't be used to enumerate which emails exist).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';
import { appForgot, formatApiErrorDetail } from '@/lib/appApi';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await appForgot(email);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Could not send reset email');
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-emerald-50 px-4">
      <Card className="w-full max-w-sm shadow-sm border-0" data-testid="forgot-password-page">
        <CardContent className="p-7">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Mail className="h-5 w-5 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Forgot password?</h1>
            <p className="text-sm text-gray-500 mt-1">
              {submitted
                ? 'Check your inbox.'
                : "Type your email and we'll send a reset link."}
            </p>
          </div>

          {submitted ? (
            <div className="space-y-4 text-sm text-gray-700" data-testid="forgot-password-confirmation">
              <p>If an account exists for <strong>{email}</strong>, you'll receive an email with a reset link within a few minutes.</p>
              <p className="text-xs text-gray-500">Didn't get it? Check your spam folder, then try again.</p>
              <Button variant="outline" className="w-full" onClick={() => { setEmail(''); setSubmitted(false); }} data-testid="forgot-password-try-again">
                Try a different email
              </Button>
            </div>
          ) : (
            <form className="space-y-3" onSubmit={onSubmit}>
              <Input
                type="email" placeholder="you@example.com" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-password-email"
              />
              <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="forgot-password-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
              </Button>
            </form>
          )}

          <p className="mt-5 text-center">
            <Link to="/portal-signin" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
              <ArrowLeft className="h-3 w-3" /> Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
